import React, { FC, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLiveQuery } from "dexie-react-hooks";
// import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Plot from 'react-plotly.js';

//HOC
import ConfigCheck from 'HOC/ConfigCheck';

//components
import LoadingScreen from 'Components/Shared/LoadingScreen';

//constants
import { CONFIG_MAP } from 'Constants/Config';

//database
import DatabaseDbs from "Containers/Database/Database.dbs";

//selectors
import { getWeatherDataIsLoading, getWeatherDataWeatherData } from 'Containers/WeatherData/WeatherData.selectors';

//style
import {
	LiveDataWrapper,
	LiveDataTile,
	SliderWrapper,
	HistoricalDataWrapper,
	HistoricalChart,
} from "./WeatherDataPage.style";
import { Slider } from '@mui/material';

interface WeatherDataProps {
	title?: string;
	weatherData: WeatherReduxType[] | null;
}

const WeatherDataPage: FC = () => {
	const isLoading = useSelector(getWeatherDataIsLoading);
	const weatherData = useSelector(getWeatherDataWeatherData);
	const historicalData = useLiveQuery(async () => weatherData
		? await DatabaseDbs.WEATHERDATA.toArray()
		: null,
	[weatherData, DatabaseDbs]);

	const [sliderDateRange, setSliderDateRange] = useState<number[]>([0, 100]);

	const sortedHistoricalData = historicalData?.sort((a, b) => {
		return a.timestamp - b.timestamp;
	});

	const firstTimestamp = sortedHistoricalData
		? sortedHistoricalData[0].timestamp
		: null;
	const lastTimestamp = sortedHistoricalData
		? sortedHistoricalData[sortedHistoricalData.length - 1].timestamp
		: null;

	const _downsample = (data: WeatherReduxType[], incFactor?: number) => {
		const factor = incFactor ?? Math.floor(data.length / 200);
		if (factor <= 1) return data;
		const result: WeatherReduxType[] = [];
		for (let i = 0; i < data.length; i += factor) {
			const chunk = data.slice(i, i + factor);
			const avg: WeatherReduxType = {
				...chunk[0],
				temperature: chunk.reduce((sum, item) => sum + item.temperature, 0) / chunk.length,
				humidity: chunk.reduce((sum, item) => sum + item.humidity, 0) / chunk.length,
				windSpeed: chunk.reduce((sum, item) => sum + item.windSpeed, 0) / chunk.length,
				solarRadiation: chunk.reduce((sum, item) => sum + item.solarRadiation, 0) / chunk.length,
				uv: chunk.reduce((sum, item) => sum + item.uv, 0) / chunk.length,
				rainRate: chunk.reduce((sum, item) => sum + item.rainRate, 0) / chunk.length,
				yearlyRain: chunk.reduce((sum, item) => sum + item.yearlyRain, 0) / chunk.length,
				barometricPressure: chunk.reduce((sum, item) => sum + item.barometricPressure, 0) / chunk.length,
			};
			result.push(avg);
		}
		return result;
	}

	const _getDateRangeFromSlider = (range: number[]) => {
		if (!firstTimestamp || !lastTimestamp) return [0, 0];
		const [startPercent, endPercent] = range;
		const startTime = firstTimestamp + ((lastTimestamp - firstTimestamp) * startPercent) / 100;
		const endTime = firstTimestamp + ((lastTimestamp - firstTimestamp) * endPercent) / 100;
		return [startTime, endTime];
	};

	const [startTime, endTime] = _getDateRangeFromSlider(sliderDateRange);
	const filteredHistoricalData = sortedHistoricalData?.filter((item) => {
		return item.timestamp >= startTime && item.timestamp <= endTime;
	});

	const downsampledHistoricalData = filteredHistoricalData
		? _downsample(filteredHistoricalData)
		: null;
	
	const timestampTenMinAgo = Date.now() - 10 * 60 * 1000;
	const timestampOneHourAgo = Date.now() - 60 * 60 * 1000;
	const tenMinWeatherData = sortedHistoricalData?.filter((item) => {
		return item.timestamp >= timestampTenMinAgo;
	});
	const tenMinAverage = _downsample(tenMinWeatherData ?? [], tenMinWeatherData?.length ?? 1);
	const oneHourWeatherData = sortedHistoricalData?.filter((item) => {
		return item.timestamp >= timestampOneHourAgo;
	});
	const oneHourAverage = _downsample(oneHourWeatherData ?? [], oneHourWeatherData?.length ?? 1);

	const _handleSliderChange = (event: Event, value: number | number[], activeThumb: number) => {
		if (Array.isArray(value)) {
			setSliderDateRange(value);
		}
	};

	if (isLoading || !weatherData) return <LoadingScreen />;

	return (
		<div className="p-6">
			<h5 style={{ float: "right", marginRight: "5px" }} className="mb-6 text-gray-600">
				{new Date(weatherData[0].timestamp).toLocaleTimeString()}
			</h5>
			<LiveData title="Live" weatherData={weatherData} />
			<LiveData title="10 min Average" weatherData={tenMinAverage} />
			<LiveData title="1 h Average" weatherData={oneHourAverage} />

			{(historicalData && historicalData?.length > 250)
				? (
					<>
						<SliderWrapper>
							<Slider
								value={sliderDateRange}
								onChange={_handleSliderChange}
								valueLabelDisplay="auto"
								valueLabelFormat={(value) => {
									const [startTime, endTime] = _getDateRangeFromSlider([value, 0]);
									return `${new Date(startTime).toLocaleDateString()}`;
								}}
								min={0}
								max={100}
								marks
								step={1}
								style={{ margin: "10px 30px" }}
							/>
						</SliderWrapper>
						<HistoricalData weatherData={downsampledHistoricalData ?? null} />
					</>
				)
				: <LoadingScreen />}
		</div>
	);
}

const LiveData: FC<WeatherDataProps> = ({ title, weatherData }) => {
	if (!weatherData || !weatherData.length) return <LoadingScreen />;

	return (
		<LiveDataWrapper>
			{/* <h5 className="mb-6 text-gray-600">{title}</h5> */}
			{[
				{ label: title, value: "" },
				{ label: "Temperature", value: `${Math.round(weatherData[0].temperature*100)/100} °C` },
				{ label: "Humidity", value: `${Math.round(weatherData[0].humidity*100)/100} %` },
				{ label: "Wind Speed", value: `${Math.round(weatherData[0].windSpeed*100)/100} km/h` },
				{ label: "Solar Radiation", value: `${Math.round(weatherData[0].solarRadiation*100)/100} W/m²` },
				{ label: "UV Index", value: Math.round(weatherData[0].uv*100)/100 },
				{ label: "Rain Rate", value: `${Math.round(weatherData[0].rainRate*100)/100} mm` },
				{ label: "Pressure", value: `${Math.round(weatherData[0].barometricPressure*100)/100} hPa` },
			].map((item, idx) => (
				<LiveDataTile key={idx}>
					<p className="text-gray-500 text-sm">{item.label}</p>
					<p className="text-lg font-bold">{item.value}</p>
				</LiveDataTile>
			))}
		</LiveDataWrapper>
	);
}

const HistoricalData: FC<WeatherDataProps> = ({ weatherData }) => {
	if (!weatherData) return null;

	const tempData = weatherData.map((item) => ({
		// x: item.timestamp,
		x: new Date(item.timestamp).toLocaleString(),
		y: item.temperature,
	}));
	const humidityData = weatherData.map((item) => ({
		// x: item.timestamp,
		x: new Date(item.timestamp).toLocaleString(),
		y: item.humidity,
	}));
	const windSpeedData = weatherData.map((item) => ({
		// x: item.timestamp,
		x: new Date(item.timestamp).toLocaleString(),
		y: item.windSpeed,
	}));
	const rainData = weatherData.map((item) => ({
		// x: item.timestamp,
		x: new Date(item.timestamp).toLocaleString(),
		y: item.yearlyRain,
	}));

	return (
		<HistoricalDataWrapper>
			<HistoricalChart>
				<Plot
					data={[{
						x: tempData.map((item) => item.x),
						y: tempData.map((item) => item.y),
						type: 'scatter',
						mode: 'lines+markers',
						marker: { color: 'red' },
					}]}
					layout={{
						title: { text: 'Temperature' },
					}}
					style={{ width: "100%", height: "100%" }}
				/>
			</HistoricalChart>
			<HistoricalChart>
				<Plot
					data={[{
						x: humidityData.map((item) => item.x),
						y: humidityData.map((item) => item.y),
						type: 'scatter',
						mode: 'lines+markers',
						marker: { color: 'red' },
					}]}
					layout={{
						title: { text: 'Humidity' },
					}}
					style={{ width: "100%", height: "100%" }}
				/>
			</HistoricalChart>
			<HistoricalChart>
				<Plot
					data={[{
						x: windSpeedData.map((item) => item.x),
						y: windSpeedData.map((item) => item.y),
						type: 'scatter',
						mode: 'lines+markers',
						marker: { color: 'red' },
					}]}
					layout={{
						title: { text: 'Wind Speed' },
					}}
					style={{ width: "100%", height: "100%" }}
				/>
			</HistoricalChart>
			<HistoricalChart>
				<Plot
					data={[{
						x: rainData.map((item) => item.x),
						y: rainData.map((item) => item.y),
						type: 'scatter',
						mode: 'lines+markers',
						marker: { color: 'blue' },
					}]}
					layout={{
						title: { text: 'Total Rain' },
					}}
					style={{ width: "100%", height: "100%" }}
				/>
			</HistoricalChart>
		</HistoricalDataWrapper>
	);
}

export default ConfigCheck(WeatherDataPage, [[CONFIG_MAP.weatherdata]]);